import {
  type IconKind,
  type TimelineColorThemesKinds,
} from '@saviynt/design-system';

import {
  CF_NEU_STL,
  CF_SUS_STL,
  INPUT_CHAR_LIMIT,
  type MessageState,
  VALID_CHIP_PATTERN,
} from '../../../shared/constants';

/**
 * Maps a status string to its display color, text, and localization key.
 * @param status - The raw status string from the API.
 * @returns An object with `statusColor`, `statusText`, and `statusTextKey`.
 */
export const getStatusKind = (
  status: string | undefined
): { statusColor: string; statusText: string; statusTextKey: MessageState } => {
  if (!status) {
    return {
      statusColor: CF_NEU_STL,
      statusText: ' ',
      statusTextKey: 'status.empty',
    };
  }

  if (status.toLowerCase().includes('enable')) {
    return {
      statusColor: CF_SUS_STL,
      statusText: 'Enabled',
      statusTextKey: 'status.enabled',
    };
  }

  if (status.toLowerCase().includes('disable')) {
    return {
      statusColor: CF_NEU_STL,
      statusText: 'Disabled',
      statusTextKey: 'status.disabled',
    };
  }

  if (status.toLowerCase().includes('inactive')) {
    return {
      statusColor: CF_NEU_STL,
      statusText: 'Inactive',
      statusTextKey: 'status.inactive',
    };
  }

  if (status.toLowerCase().includes('active')) {
    return {
      statusColor: CF_SUS_STL,
      statusText: 'Active',
      statusTextKey: 'status.active',
    };
  }

  if (status.toLowerCase().includes('delete')) {
    return {
      statusColor: CF_NEU_STL,
      statusText: 'Deleted',
      statusTextKey: 'status.deleted',
    };
  }

  return {
    statusColor: CF_NEU_STL,
    statusText: ' ',
    statusTextKey: 'status.empty',
  };
};

/**
 * Finds groups of tag keys that share the same non-empty value.
 * @param tags - A record of tag key-value pairs.
 * @returns An array of key groups where each group shares a duplicate value.
 */
const getDuplicateTagKeys = (tags: Record<string, string>): string[][] => {
  const valueToKeys: Record<string, string[] | undefined> = {};

  Object.entries(tags).forEach(([key, value]) => {
    if (value.trim() === '') {
      return;
    }

    const nVal = value.trim();

    if (!valueToKeys[nVal]) {
      valueToKeys[nVal] = [key];
    } else {
      valueToKeys[nVal].push(key);
    }
  });

  return Object.values(valueToKeys).filter(
    (keys): keys is string[] => !!keys && keys.length > 1
  );
};

/**
 * Collects all tag keys that share a duplicate non-empty value into a flat array.
 * @param tags - A record of tag key-value pairs.
 * @returns An array of keys whose values appear more than once.
 */
export const getDuplicateTagKeySet = (
  tags: Record<string, string>
): string[] => [...new Set(getDuplicateTagKeys(tags).flat())];

/**
 * Determines whether a tag value is in a critical (invalid) state.
 * @param text - The tag input value.
 * @returns `true` if the tag exceeds 50 characters or contains invalid characters.
 */
export const getTagIsCritical = (text: string) => {
  const trimmed = text.trim();

  if (trimmed.length === 0) {
    return false;
  }

  if (trimmed.length > 50 || !VALID_CHIP_PATTERN.test(trimmed)) {
    return true;
  }

  return false;
};

/**
 * Returns the validation error message for a tag value, or an empty string if valid.
 * @param text - The tag input value.
 * @returns A user-facing error message, or `''` if no error.
 */
export const getTagCriticalText = (text: string, isDuplicate = false) => {
  const trimmed = text.trim();

  if (trimmed.length === 0) {
    return '';
  }

  if (trimmed.length > 50) {
    return 'Text must be 50 characters or fewer';
  }

  if (!VALID_CHIP_PATTERN.test(trimmed)) {
    return 'Only lowercase letters, numbers, underscores, colon and hyphens are allowed';
  }

  if (isDuplicate) {
    return 'Duplicate tags are not allowed';
  }

  return '';
};

/**
 * Determines whether a name value is in a critical (invalid) state.
 * @param text - The name input value.
 * @returns `true` if the name is empty, too short, too long, or contains invalid characters.
 */
export const getNameIsCritical = (text: string) => {
  const trimmed = text.trim();

  if (
    trimmed.length === 0 ||
    trimmed.length < INPUT_CHAR_LIMIT ||
    trimmed.length > 100 ||
    !/^[a-zA-Z0-9_-]+$/.test(trimmed)
  ) {
    return true;
  }

  return false;
};

/**
 * Returns the validation error message for a name value, or an empty string if valid.
 * @param text - The name input value.
 * @returns A user-facing error message, or `''` if no error.
 */
export const getNameCriticalText = (text: string) => {
  const trimmed = text.trim();

  if (trimmed.length === 0) {
    return 'Field should not be empty';
  }

  if (trimmed.length < INPUT_CHAR_LIMIT) {
    return `Text must be ${INPUT_CHAR_LIMIT} characters or more`;
  }

  if (trimmed.length > 100) {
    return 'Text must be 100 characters or fewer';
  }

  if (!/^[a-zA-Z0-9_-]+$/.test(trimmed)) {
    return 'Only letters, numbers, underscores and hyphens are allowed';
  }

  return '';
};

/**
 * Determines whether a description value is in a critical (invalid) state.
 * Empty descriptions are considered valid (not required).
 * @param text - The description input value.
 * @returns `true` if the description is non-empty and too short or too long.
 */
export const getDescIsCritical = (text: string) => {
  const trimmed = text.trim();

  if (trimmed.length === 0) {
    return false;
  }

  if (trimmed.length < INPUT_CHAR_LIMIT || trimmed.length > 1000) {
    return true;
  }

  return false;
};

/**
 * Returns the validation error message for a description value, or an empty string if valid.
 * @param text - The description input value.
 * @returns A user-facing error message, or `''` if no error.
 */
export const getDescCriticalText = (text: string) => {
  const trimmed = text.trim();

  if (trimmed.length < INPUT_CHAR_LIMIT) {
    return `Text must be ${INPUT_CHAR_LIMIT} characters or more`;
  }

  if (trimmed.length > 1000) {
    return 'Text must be 1000 characters or fewer';
  }

  return '';
};

/**
 * Resolves the appropriate "no data" message key based on the current loading,
 * error, and search state of a filter dropdown.
 * @param isError - Whether the data fetch errored.
 * @param isLoading - Whether the data is currently loading.
 * @param searchText - The current search input value.
 * @param optionsCount - The number of options currently available.
 * @returns A `MessageState` key, or `undefined` if data should render normally.
 */
export const getNoDataText = <T extends boolean>(
  isError?: T,
  isLoading?: T,
  searchText?: string | null,
  optionsCount?: number
): MessageState | undefined => {
  if (isError) {
    return 'state.search.failed';
  }

  if (isLoading && Number(optionsCount) === 0) {
    return 'state.loading.dot';
  }

  if (!searchText) {
    return 'state.enter.one.char';
  }

  return undefined;
};

/**
 * Maps a history event type label to its timeline color theme and icon.
 * @param label - The event type string (e.g. `'Owner Added'`, `'Service Identity Delete'`).
 * @returns An object with `colorTheme` and `icon` for the Timeline component.
 */
export const getTimelineProps = (label: string) => {
  const obj: { colorTheme: TimelineColorThemesKinds; icon: IconKind } = {
    colorTheme: 'positive',
    icon: 'Check',
  };

  if (label === 'Credentials Rotated') {
    obj.icon = 'Reset';
  }

  if (label === 'Owner Added') {
    obj.icon = 'Add';
  }

  if (label === 'Owner Removed') {
    obj.colorTheme = 'negative';
    obj.icon = 'Close';
  }

  if (label === 'Service Accounts Linked') {
    obj.icon = 'Link';
  }

  if (label === 'Service Accounts Unlinked') {
    obj.colorTheme = 'negative';
    obj.icon = 'Link';
  }

  if (label === 'Service Identity Enable') {
    obj.icon = 'CheckCircleOutline';
  }

  if (label === 'Service Identity Disable') {
    obj.colorTheme = 'negative';
    obj.icon = 'CheckCircleOutline';
  }

  if (label === 'Service Identity Delete') {
    obj.colorTheme = 'negative';
    obj.icon = 'Delete';
  }

  if (label === 'Service Identity Created') {
    obj.icon = 'CreateUser';
  }

  if (label === 'Description Updated') {
    obj.icon = 'Document';
  }

  if (label === 'Tags Updated') {
    obj.icon = 'Tag';
  }

  if (label === 'Password Policy Updated') {
    obj.icon = 'Password';
  }

  return obj;
};

/**
 * Checks whether two string arrays contain the same set of values (order-independent).
 * @param arr1 - The first string array.
 * @param arr2 - The second string array.
 * @returns `true` if both arrays contain exactly the same unique values.
 */
export const haveSameStringSet = <T extends string[] = []>(
  arr1: T,
  arr2: T
) => {
  const set1 = new Set(arr1);
  const set2 = new Set(arr2);

  if (set1.size !== set2.size) {
    return false;
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const val of set1) {
    if (!set2.has(val)) {
      return false;
    }
  }

  return true;
};
